import React, { useEffect, useState } from "react";
import Layout from "../components/layouts/layout";
import DataService from "../services/api/data_services";
import { BASE_SITE_URL } from "../shared/constants";
import ogimage from "../assets/Images/ogImages/OG_Insights.png";

// Modern components
import InsightsHeroSection from "../components/ModernArticles/InsightsHeroSection";
import InsightsIntroSection from "../components/ModernArticles/InsightsIntroSection";
import InsightsCategorySection from "../components/ModernArticles/InsightsCategorySection";
import ArticlesCTASection from "../components/ModernArticles/ArticlesCTASection";
import "../components/ModernArticles/modernArticlesPage.scss";

const CATEGORY_LIMIT = 6;

const Insights = () => {
  const [articles, setArticles] = useState([]);
  const [whitePapers, setWhitePapers] = useState([]);
  const [clientStories, setClientStories] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState({
    articles: true,
    whitePapers: true,
    clientStories: true,
    podcasts: true,
  });

  useEffect(() => {
    // Fetch Articles
    DataService.getData(
      `/admin/api/post?limit=${CATEGORY_LIMIT}&page=1&type=Article&component=mini_view`,
    )
      .then((response) => {
        setArticles(response.data || []);
        setLoading((prev) => ({ ...prev, articles: false }));
      })
      .catch(() => setLoading((prev) => ({ ...prev, articles: false })));

    // Fetch White Papers
    DataService.getData(
      `/admin/api/post?limit=${CATEGORY_LIMIT}&page=1&page_id=1&type=White Paper`,
    )
      .then((response) => {
        setWhitePapers(response.data || []);
        setLoading((prev) => ({ ...prev, whitePapers: false }));
      })
      .catch(() => setLoading((prev) => ({ ...prev, whitePapers: false })));

    // Fetch Client Stories
    DataService.getData(
      `/admin/api/post?limit=${CATEGORY_LIMIT}&page=1&page_id=1&type=Client Story&component=mini_view`,
    )
      .then((response) => {
        setClientStories(response.data || []);
        setLoading((prev) => ({ ...prev, clientStories: false }));
      })
      .catch(() => setLoading((prev) => ({ ...prev, clientStories: false })));

    // Fetch Podcasts
    DataService.getData(`/admin/api/podcast?type=all`)
      .then((response) => {
        setPodcasts((response.data || []).slice(0, CATEGORY_LIMIT));
        setLoading((prev) => ({ ...prev, podcasts: false }));
      })
      .catch(() => setLoading((prev) => ({ ...prev, podcasts: false })));
  }, []);

  return (
    <Layout
      seo={{
        title: "Insights | DigiRyte",
        ogtitle: "Insights | DigiRyte",
        description:
          "Explore DigiRyte's insights to uncover proprietary digital approaches and gain valuable knowledge and expertise shared by our expert team in technology.",
        ogimage: ogimage,
        ogurl: `${BASE_SITE_URL}/insights`,
        keywords:
          "DigiRyte insights, digital approaches, technology expertise, knowledge sharing",
      }}
    >
      <div className="modern-articles-page">
        <InsightsHeroSection
          title="Insights & Knowledge Hub"
          titleHighlight="Knowledge Hub"
          subtitle="Expert-driven articles, in-depth white papers, and real client success stories - curated to help you build better products, make smarter decisions, and stay ahead in technology."
          breadcrumbs={[{ label: "Home", to: "/" }, { label: "Insights" }]}
          tabs={[
            {
              label: "All Insights",
              to: "/insights",
              active: true,
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              ),
            },
            {
              label: "Articles",
              to: "/insights/articles",
              active: false,
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              ),
            },
            {
              label: "White Papers",
              to: "/insights/white-papers",
              active: false,
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              ),
            },
          ]}
          stats={[
            { value: "50+", label: "Articles" },
            { value: "10+", label: "Topics" },
            { value: "100K+", label: "Readers" },
            { value: "15+", label: "Authors" },
          ]}
        />

        <InsightsIntroSection />

        {/* Articles Section */}
        <InsightsCategorySection
          title="Latest Articles"
          titleAccent="Articles"
          subtitle="Expert analysis on trending technologies, frameworks, and engineering best practices."
          icon={
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          }
          items={articles}
          viewAllLink="/insights/articles"
          viewAllLabel="View All Articles"
          isLoading={loading.articles}
        />

        {/* White Papers Section */}
        <InsightsCategorySection
          title="White Papers"
          titleAccent="Papers"
          subtitle="Proprietary digital approaches and deep research to accelerate your business growth."
          icon={
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          }
          items={whitePapers}
          viewAllLink="/insights/white-papers"
          viewAllLabel="View All White Papers"
          accentColor="#2563eb"
          isLoading={loading.whitePapers}
        />

        {/* Client Stories Section */}
        <InsightsCategorySection
          title="Client Stories"
          titleAccent="Stories"
          subtitle="Real-world success stories from our clients across industries and technologies."
          icon={
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
          items={clientStories}
          viewAllLink="/clients"
          viewAllLabel="View All Client Stories"
          accentColor="#059669"
          isLoading={loading.clientStories}
        />

        {/* Podcasts Section (only show if API returns data) */}
        {(loading.podcasts || podcasts.length > 0) && (
          <InsightsCategorySection
            title="Podcasts"
            titleAccent="Podcasts"
            subtitle="Listen to conversations with tech leaders and experts."
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            }
            items={podcasts}
            viewAllLink=""
            viewAllLabel=""
            accentColor="#7c3aed"
            isLoading={loading.podcasts}
          />
        )}

        <ArticlesCTASection />
      </div>
    </Layout>
  );
};

export default Insights;
